import type { ChangeExtraFields, ChangeFormField, ChangeType } from '../../types/changes';
import { isEmergencyChangeType } from './changeLabels';
import { nativeFieldVisible } from './changeFormSchema';
import {
  DEFAULT_CAMUD_WEEKDAY,
  DEFAULT_CHANGE_WINDOW_WEEKDAY,
  DEFAULT_RISK_COMMITTEE_WEEKDAY,
  isDateOnWeekday,
  isGoLiveOnWindowDay,
  nativeWeekdayConstraint,
  requiresThursdayGoLive,
  toBackendDateTime,
  weekdayName,
} from './goLiveWindow';

/**
 * Limites espelhados de `backend/app/services/changes/validators.py`.
 *
 * Servem para o `maxLength` dos inputs: o usuário é barrado ao digitar em vez de
 * escrever um texto longo, enviar e receber 422. O backend continua sendo a
 * autoridade — isto é conveniência de UI, não substituto da validação.
 */
export const CHANGE_FIELD_LIMITS = {
  title: 500,
  shortText: 255,
  longText: 20000,
  comment: 5000,
  justification: 2000,
  evidence: 500,
} as const;

export const CHANGE_MIN_TEXT_LENGTH = 3;

type NativeTextKey = keyof ChangeTextFields;

interface NativeTextSpec {
  key: NativeTextKey;
  defaultLabel: string;
  defaultRequired: boolean;
  minLength: number;
  maxLength: number;
}

/** Regras de texto alinhadas ao cadastro: só vale o que está visível na tela. */
const NATIVE_TEXT_SPECS: NativeTextSpec[] = [
  {
    key: 'title',
    defaultLabel: 'Título',
    defaultRequired: true,
    minLength: CHANGE_MIN_TEXT_LENGTH,
    maxLength: CHANGE_FIELD_LIMITS.title,
  },
  {
    key: 'description',
    defaultLabel: 'Descrição',
    defaultRequired: true,
    minLength: CHANGE_MIN_TEXT_LENGTH,
    maxLength: CHANGE_FIELD_LIMITS.longText,
  },
  {
    key: 'scope',
    defaultLabel: 'Escopo',
    defaultRequired: false,
    minLength: CHANGE_MIN_TEXT_LENGTH,
    maxLength: CHANGE_FIELD_LIMITS.longText,
  },
  {
    key: 'rollback_plan',
    defaultLabel: 'Plano de rollback',
    defaultRequired: true,
    minLength: CHANGE_MIN_TEXT_LENGTH,
    maxLength: CHANGE_FIELD_LIMITS.longText,
  },
  {
    key: 'impacted_users',
    defaultLabel: 'Usuários impactados',
    defaultRequired: true,
    minLength: 1,
    maxLength: CHANGE_FIELD_LIMITS.longText,
  },
  {
    key: 'project',
    defaultLabel: 'Projeto',
    defaultRequired: true,
    minLength: 1,
    maxLength: CHANGE_FIELD_LIMITS.shortText,
  },
];

export const findNativeField = (
  fields: ChangeFormField[] | null | undefined,
  nativeKey: string,
): ChangeFormField | undefined =>
  (fields || []).find((item) => item.kind === 'native' && item.native_key === nativeKey);

export const nativeCatalogLabel = (
  fields: ChangeFormField[] | null | undefined,
  nativeKey: string,
  fallback: string,
): string => {
  const label = (findNativeField(fields, nativeKey)?.label || '').trim();
  return label || fallback;
};

const nativeTextError = (
  form: ChangeFormValues,
  fields: ChangeFormField[] | null | undefined,
  extras: ChangeExtraFields | null | undefined,
): string | null => {
  const trimmed = trimChangeTextFields(form);
  for (const spec of NATIVE_TEXT_SPECS) {
    if (!nativeFieldVisible(fields, spec.key, form, extras || {})) continue;
    const field = findNativeField(fields, spec.key);
    const required = field ? Boolean(field.is_required) : spec.defaultRequired;
    const label = nativeCatalogLabel(fields, spec.key, spec.defaultLabel);
    const value = trimmed[spec.key];
    if (!value) {
      if (required) return `Preencha o campo obrigatório: ${label}.`;
      continue;
    }
    if (value.length < spec.minLength) {
      return `${label} deve ter ao menos ${spec.minLength} caracteres.`;
    }
    if (value.length > spec.maxLength) {
      return `${label} deve ter no máximo ${spec.maxLength} caracteres.`;
    }
  }
  return null;
};

/** Campos de texto comuns aos formulários de criação e de edição. */
export interface ChangeTextFields {
  title: string;
  description: string;
  scope: string;
  impacted_users: string;
  rollback_plan: string;
  project: string;
}

export interface ChangeFormValues extends ChangeTextFields {
  /** Janela de Go Live: dois `datetime-local`. */
  go_live_start_at: string;
  go_live_end_at: string;
  /** Agendamentos de governança: datas sem hora. */
  risk_committee_start_at: string;
  risk_committee_end_at: string;
  camud_go_no_go_date: string;
  camud_extraordinary: boolean;
  change_type: ChangeType;
  /** Vêm dos cadastros; 0 significa "nada selecionado". */
  category_id: number;
  requester_area_id: number;
  /** Grupo de mudanças escolhido no campo «Gerente imediato (Transporte)». */
  requester_manager_group_id: number;
  /** Solicitante já gravado (edição) ou escolhido na criação — alimenta o picker. */
  requester_id?: number;
  /** Tipos de subtarefa escolhidos na criação. Vêm do cadastro configurável. */
  task_type_ids: number[];
  /** SAP envia Infra Hosting no redisparo; Não SAP envia Infra Nuvem. */
  sap_classification: 'sap' | 'nao_sap' | '';
  /** Só quando SAP: Sim inclui aprovação extra configurada em Transporte. */
  sap_has_request: boolean | null;
}

export interface GoLiveOverrideState {
  isAdmin: boolean;
  forceOutside: boolean;
  forceReason: string;
}

export const trimChangeTextFields = (form: ChangeTextFields): ChangeTextFields => ({
  title: form.title.trim(),
  description: form.description.trim(),
  scope: form.scope.trim(),
  impacted_users: form.impacted_users.trim(),
  rollback_plan: form.rollback_plan.trim(),
  project: form.project.trim(),
});

/**
 * Indica se o Go Live precisa do override de admin (fora da janela de quinta).
 *
 * A janela é avaliada pelo início; o fim acompanha.
 */
export const needsGoLiveOverride = (
  form: ChangeFormValues,
  fields?: ChangeFormField[] | null,
): boolean => {
  const weekday = nativeWeekdayConstraint(fields, 'go_live_window', DEFAULT_CHANGE_WINDOW_WEEKDAY);
  if (weekday == null) return false;
  if (!requiresThursdayGoLive(form.change_type)) return false;
  if (!form.go_live_start_at) return false;
  return !isGoLiveOnWindowDay(form.go_live_start_at, weekday);
};

const isValidDate = (value: string): boolean => !Number.isNaN(new Date(value).getTime());

/** Se o início existir e o fim estiver vazio, o fim assume o início (evita bloquear Emergencial). */
export const fillGoLiveEndIfMissing = (form: ChangeFormValues): ChangeFormValues => {
  const start = (form.go_live_start_at || '').trim();
  const end = (form.go_live_end_at || '').trim();
  if (start && !end) {
    return { ...form, go_live_end_at: start };
  }
  return form;
};

/** Ao mudar o início: preenche o fim vazio ou mantém o fim alinhado se o usuário ainda não escolheu outro. */
export const patchGoLiveStart = (form: ChangeFormValues, start: string): Partial<ChangeFormValues> => {
  const end = (form.go_live_end_at || '').trim();
  const prevStart = (form.go_live_start_at || '').trim();
  if (!end || end === prevStart) {
    return { go_live_start_at: start, go_live_end_at: start };
  }
  return { go_live_start_at: start };
};

const isCurrencyExtra = (
  value: object,
): value is { currency: string; value: string } => 'currency' in value;

const isPeriodExtra = (
  value: object,
): value is { start: string; end: string } => 'start' in value || 'end' in value;

const extraSkipValueFilled = (value: ChangeExtraFields[string]): boolean => {
  if (value == null) return false;
  if (typeof value !== 'object') return Boolean(String(value).trim());
  if (isCurrencyExtra(value)) return Boolean(String(value.value || '').trim());
  if (isPeriodExtra(value)) return Boolean(value.start || value.end);
  return false;
};

/** Dispensa configurável no cadastro: campo com a flag preenchido. */
export const formSkipsAutoChecklist = (
  fields: ChangeFormField[] | null | undefined,
  form: ChangeFormValues,
  extras?: ChangeExtraFields | null,
): boolean =>
  (fields || []).some((field) => {
    if (!field.skips_auto_checklist || !field.is_visible) return false;
    if (field.kind === 'native') {
      const value = (form as unknown as Record<string, unknown>)[field.native_key || ''];
      return Boolean(String(value ?? '').trim());
    }
    return extraSkipValueFilled((extras || {})[String(field.id)]);
  });

/**
 * Valida o formulário de mudança. Retorna a mensagem de erro ou `null`.
 *
 * Fonte única para criação e edição: antes as duas telas tinham a mesma
 * sequência de checagens copiada, e ajustar uma regra deixava a outra atrás.
 * Todos os campos são obrigatórios.
 */
export const validateChangeForm = (
  form: ChangeFormValues,
  override: GoLiveOverrideState,
  fields?: ChangeFormField[] | null,
  extras?: ChangeExtraFields | null,
): string | null => {
  const textError = nativeTextError(form, fields, extras);
  if (textError) return textError;

  const extrasMap = extras || {};
  const filled = fillGoLiveEndIfMissing(form);
  const goLiveWindowLabel = isEmergencyChangeType(filled.change_type)
    ? 'da implementação'
    : `de ${nativeCatalogLabel(fields, 'go_live_window', 'Go Live')}`;
  const categoryVisible = nativeFieldVisible(fields, 'category_id', filled, extrasMap);
  const areaVisible = nativeFieldVisible(fields, 'requester_area_id', filled, extrasMap);
  const sapVisible = nativeFieldVisible(fields, 'sap_classification', filled, extrasMap);
  const goLiveVisible = nativeFieldVisible(fields, 'go_live_window', filled, extrasMap);

  if (categoryVisible && !form.category_id) {
    return `Selecione ${nativeCatalogLabel(fields, 'category_id', 'a categoria')}.`;
  }
  if (areaVisible && !form.requester_area_id) {
    return `Selecione ${nativeCatalogLabel(fields, 'requester_area_id', 'a área solicitante')}.`;
  }
  const managerPickVisible = nativeFieldVisible(
    fields,
    'requester_manager_group_id',
    form,
    extrasMap,
  );
  const managerPickField = findNativeField(fields, 'requester_manager_group_id');
  if (
    managerPickVisible &&
    managerPickField?.is_required &&
    !form.requester_manager_group_id
  ) {
    return `Selecione ${nativeCatalogLabel(fields, 'requester_manager_group_id', 'o gerente imediato (Transporte)')}.`;
  }
  if (sapVisible && form.sap_classification !== 'sap' && form.sap_classification !== 'nao_sap') {
    return `Informe ${nativeCatalogLabel(fields, 'sap_classification', 'se a mudança é SAP ou Não SAP')}.`;
  }
  if (goLiveVisible) {
    if (!filled.go_live_start_at || !filled.go_live_end_at) {
      return `Informe o início e o fim ${goLiveWindowLabel}.`;
    }
    if (!isValidDate(filled.go_live_start_at) || !isValidDate(filled.go_live_end_at)) {
      return isEmergencyChangeType(filled.change_type)
        ? 'Data de implementação inválida.'
        : 'Data de Go Live inválida.';
    }
    if (new Date(filled.go_live_end_at) < new Date(filled.go_live_start_at)) {
      return isEmergencyChangeType(filled.change_type)
        ? 'O fim da implementação precisa ser maior ou igual ao início.'
        : 'O fim do Go Live precisa ser maior ou igual ao início.';
    }
  }

  const skipChecklist = formSkipsAutoChecklist(fields || [], filled, extras || {});
  const riskVisible = nativeFieldVisible(fields, 'risk_committee_window', filled, extras || {});
  const camudVisible = nativeFieldVisible(fields, 'camud_go_no_go_date', filled, extras || {});
  const riskWeekday = nativeWeekdayConstraint(
    fields,
    'risk_committee_window',
    DEFAULT_RISK_COMMITTEE_WEEKDAY,
  );
  const camudWeekday = nativeWeekdayConstraint(
    fields,
    'camud_go_no_go_date',
    DEFAULT_CAMUD_WEEKDAY,
  );
  const goLiveWeekday = nativeWeekdayConstraint(
    fields,
    'go_live_window',
    DEFAULT_CHANGE_WINDOW_WEEKDAY,
  );

  if (riskVisible && !skipChecklist && form.risk_committee_start_at) {
    if (!isValidDate(form.risk_committee_start_at)) {
      return 'Data de agendamento inválida.';
    }
    if (!isDateOnWeekday(form.risk_committee_start_at, riskWeekday)) {
      const day = weekdayName(riskWeekday);
      const fieldLabel = nativeCatalogLabel(fields, 'risk_committee_window', 'Riscos e Impactos');
      return day ? `${fieldLabel} deve cair numa ${day}.` : 'Data de agendamento inválida.';
    }
  }
  if (camudVisible && form.camud_go_no_go_date) {
    if (!isValidDate(form.camud_go_no_go_date)) {
      return 'Data de agendamento inválida.';
    }
    if (!isDateOnWeekday(form.camud_go_no_go_date, camudWeekday)) {
      const day = weekdayName(camudWeekday);
      const fieldLabel = nativeCatalogLabel(fields, 'camud_go_no_go_date', 'CAMUD Go/No-Go');
      return day ? `${fieldLabel} deve cair numa ${day}.` : 'Data de agendamento inválida.';
    }
  }

  if (needsGoLiveOverride(form, fields)) {
    const day = weekdayName(goLiveWeekday) || 'quinta-feira';
    if (!override.isAdmin) {
      return `Go Live de Normal/Padrão deve cair em ${day}.`;
    }
    if (!override.forceOutside || !override.forceReason.trim()) {
      return `Go Live fora de ${day}: marque forçar e informe o motivo (somente admin).`;
    }
  }

  return null;
};

/** Monta o corpo enviado ao backend, com texto aparado e data em hora local. */
export const buildChangeFormPayload = (
  form: ChangeFormValues,
  override: GoLiveOverrideState,
  fields?: ChangeFormField[] | null,
  extras?: ChangeExtraFields | null,
): Record<string, unknown> => {
  const filled = fillGoLiveEndIfMissing(form);
  const forcing = needsGoLiveOverride(filled, fields) && override.isAdmin && override.forceOutside;
  const skipChecklist = formSkipsAutoChecklist(fields || [], filled, extras || {});
  const extrasMap = extras || {};
  const titleVisible = nativeFieldVisible(fields, 'title', filled, extrasMap);
  const descriptionVisible = nativeFieldVisible(fields, 'description', filled, extrasMap);
  const scopeVisible = nativeFieldVisible(fields, 'scope', filled, extrasMap);
  const rollbackVisible = nativeFieldVisible(fields, 'rollback_plan', filled, extrasMap);
  const usersVisible = nativeFieldVisible(fields, 'impacted_users', filled, extrasMap);
  const projectVisible = nativeFieldVisible(fields, 'project', filled, extrasMap);
  const riskVisible = nativeFieldVisible(fields, 'risk_committee_window', filled, extrasMap);
  const camudVisible = nativeFieldVisible(fields, 'camud_go_no_go_date', filled, extrasMap);
  const hideRisk = !riskVisible || skipChecklist;
  const requestVisible = nativeFieldVisible(fields, 'sap_has_request', filled, extrasMap);
  const trimmed = trimChangeTextFields(filled);
  const sapHasRequest =
    form.sap_classification !== 'sap'
      ? null
      : !requestVisible
        ? false
        : form.sap_has_request;
  return {
    ...trimmed,
    title: titleVisible ? trimmed.title : null,
    description: descriptionVisible ? trimmed.description : null,
    scope: scopeVisible ? trimmed.scope : null,
    rollback_plan: rollbackVisible ? trimmed.rollback_plan : null,
    impacted_users: usersVisible ? trimmed.impacted_users : null,
    project: projectVisible ? trimmed.project : null,
    change_type: form.change_type,
    category_id: form.category_id,
    requester_area_id: form.requester_area_id,
    requester_manager_group_id: nativeFieldVisible(fields, 'requester_manager_group_id', form, extrasMap)
      ? form.requester_manager_group_id || null
      : null,
    sap_classification: form.sap_classification || null,
    sap_has_request: sapHasRequest,
    task_type_ids: form.task_type_ids || [],
    go_live_start_at: toBackendDateTime(filled.go_live_start_at),
    go_live_end_at: toBackendDateTime(filled.go_live_end_at),
    // Campos DATE no banco: YYYY-MM-DD. Ocultos no formulário (condicional/cadastro) não vão no payload.
    risk_committee_start_at: hideRisk ? null : form.risk_committee_start_at || null,
    risk_committee_end_at: hideRisk ? null : form.risk_committee_start_at || null,
    camud_go_no_go_date: camudVisible ? form.camud_go_no_go_date || null : null,
    camud_extraordinary: false,
    force_go_live_outside_window: forcing,
    force_go_live_reason: forcing ? override.forceReason.trim() : undefined,
  };
};
